/**
 * 견적서 관련 API 서비스
 * 새 스키마: estimates 테이블 기준
 */

import { createBrowserClient } from '@/supabase'
import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  EstimateRow,
  UpsertEstimateDraftParams,
  SubmitEstimateParams,
  DecideEstimateParams,
  EstimateStatus,
  ClientEstimateDetailResponse,
  ProposerInfo,
  EstimateRequestInfo,
  CounselSummary,
  MilestoneRow,
} from '@/types/estimate'
import { VALID_ESTIMATE_STATUS_TRANSITIONS } from '@/types/estimate'

/**
 * Direct Flow용: 견적서 Draft 생성 또는 조회 (서버 사이드 전용)
 * 
 * 핵심 규칙:
 * - Direct Flow: request_id=null
 * - 기존 DRAFT/SUBMITTED 견적이 있으면 반환
 * - 없으면 새로 생성
 * 
 * @param params 파라미터
 * @returns 견적서 정보
 */
export async function createOrGetEstimateDraft(params: {
  supabase: SupabaseClient
  counselId: number
  actorProfileId: string
  proposerType: 'TEAM' | 'INDIVIDUAL'
  proposerTeamId?: number
  proposerProfileId?: string
}): Promise<EstimateRow> {
  const { supabase, counselId, actorProfileId, proposerType, proposerTeamId, proposerProfileId } = params

  // 1. proposer 결정 및 검증
  if (proposerType === 'TEAM' && !proposerTeamId) {
    throw new Error('팀 ID가 필요합니다.')
  }
  if (proposerType === 'INDIVIDUAL' && !proposerProfileId) {
    throw new Error('프로필 ID가 필요합니다.')
  }

  // 2. 권한 확인
  if (proposerType === 'TEAM') {
    const { data: team } = await supabase
      .from('teams')
      .select('manager_profile_id')
      .eq('id', proposerTeamId!)
      .eq('manager_profile_id', actorProfileId)
      .maybeSingle()

    if (!team) {
      throw new Error('팀 매니저 권한이 필요합니다.')
    }
  } else {
    if (proposerProfileId !== actorProfileId) {
      throw new Error('권한이 없습니다.')
    }
  }

  // 3. counsel 존재 확인
  const { data: counsel, error: counselError } = await supabase
    .from('counsel')
    .select('counsel_id, client_id')
    .eq('counsel_id', counselId)
    .is('deleted_at', null)
    .maybeSingle()

  if (counselError || !counsel) {
    throw new Error('프로젝트를 찾을 수 없습니다.')
  }

  const counselObj = counsel as any

  // 4. 기존 견적 조회 (DRAFT 또는 SUBMITTED, Direct Flow만: request_id=null)
  const existingQuery: any = {
    counsel_id: counselId,
    proposer_type: proposerType,
    request_id: null, // Direct Flow만
  }

  if (proposerType === 'TEAM') {
    existingQuery.proposer_team_id = proposerTeamId
  } else {
    existingQuery.proposer_profile_id = proposerProfileId
  }

  const { data: existing } = await supabase
    .from('estimates')
    .select('*')
    .match(existingQuery)
    .in('status', ['DRAFT', 'SUBMITTED'])
    .maybeSingle()

  if (existing) {
    return existing as EstimateRow
  }

  // 5. 새 견적 생성 (request_id=null, Direct Flow)
  const estimateData: any = {
    counsel_id: counselId,
    requester_client_id: counselObj.client_id,
    proposer_type: proposerType,
    proposer_team_id: proposerType === 'TEAM' ? proposerTeamId : null,
    proposer_profile_id: proposerType === 'INDIVIDUAL' ? proposerProfileId : null,
    request_id: null, // Direct Flow
    status: 'DRAFT',
    submitted_by_profile_id: actorProfileId,
  }

  const { data: created, error: insertError } = await supabase
    .from('estimates')
    .insert(estimateData)
    .select()
    .single()

  if (insertError) {
    throw new Error(`견적서 생성 실패: ${insertError.message}`)
  }

  return created as EstimateRow
}

/**
 * 견적 Draft 업서트
 * 
 * 핵심 규칙: 동일 counsel_id + 동일 제출자 조합은 1개만 존재 (upsert 패턴)
 * 
 * @param params Draft 업서트 파라미터
 * @returns 생성/업데이트된 견적서 정보
 */
export async function upsertEstimateDraft(
  params: UpsertEstimateDraftParams
): Promise<EstimateRow> {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // proposer_type에 따른 필수 필드 검증
  if (params.proposerType === 'TEAM' && !params.proposerTeamId) {
    throw new Error('팀 제출 시 proposerTeamId가 필요합니다.')
  }
  if (params.proposerType === 'INDIVIDUAL' && !params.proposerProfileId) {
    throw new Error('개인 제출 시 proposerProfileId가 필요합니다.')
  }

  // 권한 확인
  if (params.proposerType === 'TEAM') {
    // 팀 매니저 확인
    const { data: account } = await supabase
      .from('accounts')
      .select('profile_id')
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .maybeSingle()

    if (!account) {
      throw new Error('프리랜서 프로필을 찾을 수 없습니다.')
    }

    const { data: team } = await supabase
      .from('teams')
      .select('id, manager_profile_id')
      .eq('id', params.proposerTeamId!)
      .eq('manager_profile_id', (account as any).profile_id)
      .maybeSingle()

    if (!team) {
      throw new Error('팀 매니저 권한이 필요합니다.')
    }
  } else if (params.proposerType === 'INDIVIDUAL') {
    // 개인 프리랜서 확인
    const { data: account } = await supabase
      .from('accounts')
      .select('profile_id')
      .eq('user_id', user.id)
      .eq('profile_id', params.proposerProfileId!)
      .is('deleted_at', null)
      .maybeSingle()

    if (!account) {
      throw new Error('권한이 없습니다.')
    }
  }

  // counsel 존재 확인 및 client_id 조회
  const { data: counsel, error: counselError } = await supabase
    .from('counsel')
    .select('counsel_id, client_id')
    .eq('counsel_id', params.counselId)
    .is('deleted_at', null)
    .maybeSingle()

  if (counselError || !counsel) {
    throw new Error('프로젝트를 찾을 수 없습니다.')
  }

  const counselObj = counsel as any

  // 기존 견적서 확인 (동일 counsel + 동일 제출자)
  const existingQuery: any = {
    counsel_id: params.counselId,
    proposer_type: params.proposerType,
  }

  if (params.proposerType === 'TEAM') {
    existingQuery.proposer_team_id = params.proposerTeamId
  } else {
    existingQuery.proposer_profile_id = params.proposerProfileId
  }

  const { data: existing } = await supabase
    .from('estimates')
    .select('*')
    .match(existingQuery)
    .maybeSingle()

  // 제출한 사람의 profile_id 조회 (accounts.profile_id, auth.uid 아님)
  const { data: account } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!account) {
    throw new Error('프리랜서 프로필을 찾을 수 없습니다.')
  }

  const accountObj = account as any

  const estimateData: any = {
    counsel_id: params.counselId,
    requester_client_id: counselObj.client_id,
    proposer_type: params.proposerType,
    proposer_team_id: params.proposerType === 'TEAM' ? params.proposerTeamId : null,
    proposer_profile_id: params.proposerType === 'INDIVIDUAL' ? params.proposerProfileId : null,
    submitted_by_profile_id: accountObj.profile_id, // 제출한 사람의 profile_id (accounts.profile_id)
    request_id: params.requestId || null,
    total_amount: params.totalAmount || null,
    detail: params.detail || null,
    status: 'DRAFT',
    updated_at: new Date().toISOString(),
  }

  let result: EstimateRow

  if (existing) {
    // 업데이트 (DRAFT 상태만 업데이트 가능)
    const existingObj = existing as any
    if (existingObj.status !== 'DRAFT') {
      throw new Error('제출된 견적서는 수정할 수 없습니다.')
    }

    const { data: updated, error: updateError } = await supabase
      .from('estimates')
      .update(estimateData)
      .eq('id', existingObj.id)
      .select()
      .single()

    if (updateError) {
      throw new Error(`견적서 업데이트 실패: ${updateError.message}`)
    }

    result = updated as unknown as EstimateRow
  } else {
    // 생성
    const { data: created, error: insertError } = await supabase
      .from('estimates')
      .insert(estimateData)
      .select()
      .single()

    if (insertError) {
      throw new Error(`견적서 생성 실패: ${insertError.message}`)
    }

    result = created as unknown as EstimateRow
  }

  return result
}

/**
 * 견적 제출
 * 
 * @param params 제출 파라미터
 */
export async function submitEstimate(
  params: SubmitEstimateParams
): Promise<void> {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 견적서 조회 및 권한 확인
  const { data: estimate, error: estimateError } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', params.estimateId)
    .maybeSingle()

  if (estimateError || !estimate) {
    throw new Error('견적서를 찾을 수 없습니다.')
  }

  const estimateObj = estimate as any

  // 제출자 권한 확인
  const { data: account } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!account) {
    throw new Error('프리랜서 프로필을 찾을 수 없습니다.')
  }

  const profileId = (account as any).profile_id

  let hasPermission = false

  if (estimateObj.proposer_type === 'TEAM') {
    // 팀 매니저 확인
    const { data: team } = await supabase
      .from('teams')
      .select('manager_profile_id')
      .eq('id', estimateObj.proposer_team_id)
      .eq('manager_profile_id', profileId)
      .maybeSingle()

    hasPermission = !!team
  } else {
    // 개인 프리랜서 확인
    hasPermission = estimateObj.proposer_profile_id === profileId
  }

  if (!hasPermission) {
    throw new Error('견적서를 제출할 권한이 없습니다.')
  }

  // 상태 전이 검증: DRAFT가 아니면 제출 불가
  if (estimateObj.status !== 'DRAFT') {
    throw new Error('DRAFT 상태의 견적서만 제출할 수 있습니다. 이미 제출된 견적서는 수정할 수 없습니다.')
  }

  // 같은 counsel_id + proposer 조합으로 SUBMITTED 이상이 존재하는지 확인
  const existingSubmittedQuery = supabase
    .from('estimates')
    .select('id, status')
    .eq('counsel_id', estimateObj.counsel_id)
    .in('status', ['SUBMITTED', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'])

  if (estimateObj.proposer_type === 'TEAM') {
    existingSubmittedQuery.eq('proposer_type', 'TEAM')
      .eq('proposer_team_id', estimateObj.proposer_team_id)
  } else {
    existingSubmittedQuery.eq('proposer_type', 'INDIVIDUAL')
      .eq('proposer_profile_id', estimateObj.proposer_profile_id)
  }

  const { data: existingSubmitted, error: existingError } = await existingSubmittedQuery

  if (existingError) {
    throw new Error(`기존 견적 확인 중 오류가 발생했습니다: ${existingError.message}`)
  }

  // 현재 견적을 제외하고 SUBMITTED 이상이 존재하는지 확인
  const hasExistingSubmitted = existingSubmitted?.some(
    (e: any) => e.id !== params.estimateId && ['SUBMITTED', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'].includes(e.status)
  )

  if (hasExistingSubmitted) {
    throw new Error('이미 제출한 견적이 존재합니다. 같은 프로젝트에 대해 여러 번 제출할 수 없습니다.')
  }

  // request_id 분기 처리
  if (estimateObj.request_id) {
    // Invite Flow: request.status=ACCEPTED 검증
    const { data: request, error: requestError } = await supabase
      .from('estimate_requests')
      .select('status')
      .eq('id', estimateObj.request_id)
      .maybeSingle()

    if (requestError || !request) {
      throw new Error('견적 요청을 찾을 수 없습니다.')
    }

    const requestObj = request as any
    if (requestObj.status !== 'ACCEPTED') {
      throw new Error('견적 요청이 ACCEPTED 상태가 아닙니다. 수락된 요청에 대해서만 견적서를 제출할 수 있습니다.')
    }
  }
  // Direct Flow: request_id 없음, 검증 생략 (향후 counsel 상태 체크 추가 가능)

  // 상태 업데이트 (제출 시점의 제출자 기록)
  const { error: updateError } = await supabase
    .from('estimates')
    .update({
      status: 'SUBMITTED',
      submitted_at: new Date().toISOString(),
      submitted_by_profile_id: profileId, // 제출 시점의 제출자 기록
      updated_at: new Date().toISOString(),
    } as any)
    .eq('id', params.estimateId)

  if (updateError) {
    throw new Error(`견적서 제출 실패: ${updateError.message}`)
  }

  // 제출 알림 생성 (옵션)
  try {
    const { createNotification } = await import('../notifications/notification.service')
    
    await createNotification({
      type: 'ESTIMATE_SUBMITTED',
      senderType: estimateObj.proposer_type === 'TEAM' ? 'TEAM' : 'FREELANCER',
      senderTeamId: estimateObj.proposer_type === 'TEAM' ? estimateObj.proposer_team_id : undefined,
      senderProfileId: estimateObj.proposer_type === 'INDIVIDUAL' ? estimateObj.proposer_profile_id : undefined,
      targetClientId: estimateObj.requester_client_id,
      counselId: estimateObj.counsel_id,
      dedupKey: `notification:estimate_submitted:${params.estimateId}`,
      payload: { estimateId: params.estimateId },
    })
  } catch (notificationError) {
    console.warn('제출 알림 생성 실패:', notificationError)
  }
}

/**
 * 견적 채택/미채택 결정 (클라이언트용)
 * 
 * @param params 결정 파라미터
 */
export async function decideEstimate(
  params: DecideEstimateParams
): Promise<void> {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 견적서 조회 및 권한 확인
  const { data: estimate, error: estimateError } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', params.estimateId)
    .eq('requester_client_id', user.id)
    .maybeSingle()

  if (estimateError || !estimate) {
    throw new Error('견적서를 찾을 수 없거나 권한이 없습니다.')
  }

  const estimateObj = estimate as any

  // 상태 전이 검증
  if (estimateObj.status !== 'SUBMITTED') {
    throw new Error('제출된 견적서만 채택/미채택할 수 있습니다.')
  }

  const newStatus: EstimateStatus = params.decision === 'ACCEPT' ? 'ACCEPTED' : 'REJECTED'
  const validTransitions = VALID_ESTIMATE_STATUS_TRANSITIONS[estimateObj.status as EstimateStatus]

  if (!validTransitions.includes(newStatus)) {
    throw new Error(`잘못된 상태 전이: ${estimateObj.status} → ${newStatus}`)
  }

  // [중요] 1. 상태 업데이트 먼저 (트랜잭션 순서 중요)
  const { error: updateError } = await supabase
    .from('estimates')
    .update({
      status: newStatus,
      decided_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as any)
    .eq('id', params.estimateId)

  if (updateError) {
    throw new Error(`견적서 결정 실패: ${updateError.message}`)
  }

  // [추가] 2. 성공한 경우에만 채팅방에 메시지 생성 (RPC 호출)
  try {
    // Thread 조회 (estimate_id 또는 estimate_request_id로)
    const { getThreadByEstimateId, getThreadByEstimateRequestId } = await import('../threads/thread-admin.service')
    
    let threadId: string | null = null
    
    // estimate_id로 Thread 조회 시도 (DIRECT 견적)
    if (estimateObj.id) {
      const thread = await getThreadByEstimateId(supabase, estimateObj.id)
      if (thread) {
        threadId = thread.id
      }
    }
    
    // estimate_request_id로 Thread 조회 시도 (요청 기반 견적)
    if (!threadId && estimateObj.request_id) {
      const thread = await getThreadByEstimateRequestId(supabase, estimateObj.request_id)
      if (thread) {
        threadId = thread.id
      }
    }

    // Thread가 있으면 메시지 생성
    if (threadId) {
      const flowEvent = params.decision === 'ACCEPT' ? 'ESTIMATE_ACCEPTED' : 'ESTIMATE_REJECTED'
      const metadata: Record<string, any> = {}
      
      if (params.decision === 'ACCEPT') {
        // 수락 시: next_action 정보 (기본값: CONTRACT)
        metadata.next_action = params.nextAction || 'CONTRACT'
        if (params.nextActionUrl) {
          metadata.next_action_url = params.nextActionUrl
        }
      } else {
        // 거절 시: 거절 사유 및 재협의 옵션
        if (params.rejectionReason) {
          metadata.rejection_reason = params.rejectionReason
          // MVP: 기본값은 true (모두 공개)
          metadata.is_reason_public = params.isReasonPublic !== false
        }
        // 재협의 가능 여부 (기본값: true)
        if (params.canRenegotiate !== undefined) {
          metadata.can_renegotiate = params.canRenegotiate
        }
      }

      await (supabase.rpc as any)('rpc_seed_thread_card', {
        p_thread_id: threadId,
        p_flow_event: flowEvent,
        p_estimate_id: params.estimateId,
        p_metadata: metadata,
      })

      const { applyThreadStateTransition } = await import('../threads/thread-admin.service')
      await applyThreadStateTransition(supabase, {
        flowEvent,
        threadId,
      })
    }
  } catch (messageError) {
    console.warn('채팅 메시지 생성 실패 (치명적이지 않음):', messageError)
    // 메시지 생성 실패는 치명적이지 않으므로 경고만 하고 계속 진행
  }

  // 견적 수락 시 처리
  if (params.decision === 'ACCEPT') {
    // ✅ 조건 변경: 수락(ACCEPT)했고 + 클라이언트가 "모집 마감"을 원할 때만 상태 변경
    if (params.shouldCloseProject) {
      const { error: counselUpdateError } = await supabase
        .from('counsel')
        .update({
          counsel_status: 'matched', // 모집 마감 처리
          updated_at: new Date().toISOString(),
        } as any)
        .eq('counsel_id', estimateObj.counsel_id)
        .eq('client_id', user.id)

      if (counselUpdateError) {
        console.error('프로젝트 상태 업데이트 실패:', counselUpdateError)
        // 프로젝트 상태 업데이트 실패는 치명적이지 않으므로 경고만 하고 계속 진행
        // 하지만 로그는 error 레벨로 남김
      }
    }

    // 채택 알림 생성 (옵션)
    try {
      const { createNotification } = await import('../notifications/notification.service')
      
      await createNotification({
        type: 'ESTIMATE_ACCEPTED',
        senderType: 'CLIENT',
        senderClientId: user.id,
        targetTeamId: estimateObj.proposer_type === 'TEAM' ? estimateObj.proposer_team_id : undefined,
        targetProfileId: estimateObj.proposer_type === 'INDIVIDUAL' ? estimateObj.proposer_profile_id : undefined,
        counselId: estimateObj.counsel_id,
        dedupKey: `notification:estimate_accepted:${params.estimateId}`,
        payload: { estimateId: params.estimateId },
      })
    } catch (notificationError) {
      console.warn('채택 알림 생성 실패:', notificationError)
    }
  }
}

/**
 * 견적서 조회 (클라이언트용)
 * 
 * @param counselId 프로젝트 ID (옵션)
 * @param status 필터 상태 (옵션)
 * @returns 견적서 목록 (is_read 필드 포함)
 */
export async function getClientEstimates(
  counselId?: number,
  status?: EstimateStatus
) {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  let query = supabase
    .from('estimates')
    .select('*')
    .eq('requester_client_id', user.id)
    .order('created_at', { ascending: false })

  if (counselId) {
    query = query.eq('counsel_id', counselId)
  }

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`견적서 조회 실패: ${error.message}`)
  }

  const estimates = (data || []) as unknown as EstimateRow[]

  // 읽음 상태 조회
  if (estimates.length > 0) {
    const estimateIds = estimates.map(e => e.id)
    const { data: reads } = await supabase
      .from('estimate_reads')
      .select('estimate_id, read_at')
      .eq('user_id', user.id)
      .in('estimate_id', estimateIds)

    const readRows = (reads || []) as Array<{ estimate_id: number; read_at: string | null }>
    const readsMap = new Map(
      readRows.map((r) => [r.estimate_id, r.read_at])
    )

    // 각 견적에 is_read 필드 추가
    return estimates.map(est => ({
      ...est,
      is_read: readsMap.has(est.id),
      read_at: readsMap.get(est.id) || null,
    })) as unknown as (EstimateRow & { is_read: boolean; read_at: string | null })[]
  }

  return estimates.map(est => ({
    ...est,
    is_read: false,
    read_at: null,
  })) as unknown as (EstimateRow & { is_read: boolean; read_at: string | null })[]
}

/**
 * 견적서 조회 (프리랜서/팀용)
 * 
 * @param counselId 프로젝트 ID (옵션)
 * @param status 필터 상태 (옵션)
 * @returns 견적서 목록
 */
export async function getFreelancerEstimates(
  counselId?: number,
  status?: EstimateStatus
) {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 프리랜서 프로필 조회
  const { data: account } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!account) {
    return []
  }

  const profileId = (account as any).profile_id

  // 내가 속한 팀 조회
  const { data: teams } = await supabase
    .from('team_members')
    .select('team_id')
    .eq('profile_id', profileId)
    .eq('status', 'active')

  const teamIds = teams?.map((t: any) => t.team_id) || []

  // 견적서 조회: 내가 제출한 것 (개인 또는 내 팀)
  let query = supabase
    .from('estimates')
    .select('*')
    .or(
      `proposer_profile_id.eq.${profileId},proposer_team_id.in.(${teamIds.join(',')})`
    )
    .order('created_at', { ascending: false })

  if (counselId) {
    query = query.eq('counsel_id', counselId)
  }

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`견적서 조회 실패: ${error.message}`)
  }

  return (data || []) as unknown as EstimateRow[]
}

/**
 * 프리랜서용 견적서 상세 조회 (편집용)
 * 
 * @param estimateId 견적서 ID
 * @returns 견적서 상세 정보 (estimate + milestones)
 */
export async function getFreelancerEstimateDetail(
  estimateId: number
): Promise<{ estimate: EstimateRow; milestones: MilestoneRow[] }> {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 프리랜서 프로필 조회
  const { data: account } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!account) {
    throw new Error('프로필을 찾을 수 없습니다.')
  }

  const profileId = (account as any).profile_id

  // 1. 견적서 조회 (권한 확인: proposer_profile_id 또는 proposer_team_id)
  const { data: estimate, error: estimateError } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', estimateId)
    .maybeSingle()

  if (estimateError || !estimate) {
    throw new Error('견적서를 찾을 수 없습니다.')
  }

  const estimateObj = estimate as any

  // 권한 확인: 개인 견적서는 proposer_profile_id, 팀 견적서는 team_members 확인
  if (estimateObj.proposer_type === 'INDIVIDUAL') {
    if (estimateObj.proposer_profile_id !== profileId) {
      throw new Error('견적서를 조회할 권한이 없습니다.')
    }
  } else if (estimateObj.proposer_type === 'TEAM') {
    const { data: teamMember } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('team_id', estimateObj.proposer_team_id)
      .eq('profile_id', profileId)
      .eq('status', 'active')
      .maybeSingle()

    if (!teamMember) {
      throw new Error('견적서를 조회할 권한이 없습니다.')
    }
  }

  // 2. 마일스톤 조회
  const { data: milestones, error: milestonesError } = await supabase
    .from('milestones')
    .select('*')
    .eq('estimate_id', estimateId)
    .order('position', { ascending: true })

  if (milestonesError) {
    console.warn('마일스톤 조회 실패:', milestonesError)
  }

  return {
    estimate: estimateObj as unknown as EstimateRow,
    milestones: (milestones || []) as unknown as MilestoneRow[],
  }
}

/**
 * 고객용 견적서 상세 조회
 * 
 * @param estimateId 견적서 ID
 * @returns 견적서 상세 정보 (estimate + milestones + proposer + request + counsel 요약)
 */
export async function getClientEstimateDetail(
  estimateId: number
): Promise<ClientEstimateDetailResponse> {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 1. 견적서 조회 (권한 확인: requester_client_id)
  const { data: estimate, error: estimateError } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', estimateId)
    .eq('requester_client_id', user.id)
    .maybeSingle()

  if (estimateError || !estimate) {
    throw new Error('견적서를 찾을 수 없거나 권한이 없습니다.')
  }

  const estimateObj = estimate as any

  // 2. 마일스톤 조회
  const { data: milestones, error: milestonesError } = await supabase
    .from('milestones')
    .select('*')
    .eq('estimate_id', estimateId)
    .order('position', { ascending: true })

  if (milestonesError) {
    console.warn('마일스톤 조회 실패:', milestonesError)
  }

  // 3. 제출자 정보 조회
  let proposer: ProposerInfo | null = null

  if (estimateObj.proposer_type === 'TEAM') {
    const { data: team } = await supabase
      .from('teams')
      .select('id, name, manager_profile_id')
      .eq('id', estimateObj.proposer_team_id)
      .maybeSingle()

    if (team) {
      // 매니저 정보 조회
      const teamObj = team as any
      let manager = null
      if (teamObj.manager_profile_id) {
        const { data: managerData } = await supabase
          .from('accounts')
          .select('profile_id, username, bio')
          .eq('profile_id', teamObj.manager_profile_id)
          .is('deleted_at', null)
          .maybeSingle()
        manager = managerData
      }

      proposer = {
        type: 'TEAM',
        teamId: teamObj.id,
        name: teamObj.name || undefined,
        manager: manager && (manager as any).username ? {
          profileId: (manager as any).profile_id,
          username: (manager as any).username,
          bio: (manager as any).bio,
        } : null,
        contact: {
          phone: null,
          email: null,
          website: null,
        },
      }
    }
  } else if (estimateObj.proposer_type === 'INDIVIDUAL') {
    // INDIVIDUAL
    const { data: profile } = await supabase
      .from('accounts')
      .select('profile_id, username, bio')
      .eq('profile_id', estimateObj.proposer_profile_id)
      .is('deleted_at', null)
      .maybeSingle()

    if (profile) {
      const profileObj = profile as any
      proposer = {
        type: 'INDIVIDUAL',
        profileId: profileObj.profile_id,
        username: profileObj.username || undefined,
        bio: profileObj.bio,
      }
    }
  }

  // 4. 견적 요청 정보 조회 (request_id가 있는 경우)
  let request: EstimateRequestInfo | null = null
  if (estimateObj.request_id) {
    const { data: requestData } = await supabase
      .from('estimate_requests')
      .select('id, status, message, created_at, responded_at')
      .eq('id', estimateObj.request_id)
      .maybeSingle()

    if (requestData) {
      const requestDataObj = requestData as any
      request = {
        id: requestDataObj.id,
        status: requestDataObj.status,
        message: requestDataObj.message,
        createdAt: requestDataObj.created_at,
        respondedAt: requestDataObj.responded_at,
      }
    }
  }

  // 5. 프로젝트(counsel) 요약 정보 조회
  const { data: counsel } = await supabase
    .from('counsel')
    .select('counsel_id, title, outline, start_date, period, cost, field, work_type, reference_service')
    .eq('counsel_id', estimateObj.counsel_id)
    .maybeSingle()

  return {
    estimate: estimateObj as unknown as EstimateRow,
    milestones: (milestones || []) as unknown as MilestoneRow[],
    proposer,
    request,
    counsel: counsel ? (() => {
      const counselObj = counsel as any
      return {
        counselId: counselObj.counsel_id,
        title: counselObj.title,
        outline: counselObj.outline,
        startDate: counselObj.start_date,
        dueDate: null, // counsel 테이블에 due_date 컬럼이 없음, period enum으로 기간 정보 제공
        cost: counselObj.cost,
        field: counselObj.field,
        period: counselObj.period, // 기간 정보 (enum: '1개월 이하', '1개월 ~ 3개월' 등)
        workType: counselObj.work_type, // 근무 형태 (ONSITE, REMOTE, HYBRID)
        referenceService: counselObj.reference_service, // 참고 서비스
      }
    })() : null,
  }
}
